import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { errorText } from "../../i18n/serverErrors";
import { shopModeApi } from "../../api/shopMode";
import type {
  AssignableUser,
  FloorDeliveryTodayWorkItem,
  FloorTodayWorkItem,
  Role,
  Task,
  TaskSchedule,
} from "../../api/types";
import { useT } from "../../i18n/LanguageProvider";
import { intlLocale } from "../../i18n/languages";
import { t as translate, type TranslateFn } from "../../i18n/t";
import { useShopMode } from "../../state/ShopModeContext";
import { parseFlexibleTime24 } from "../../utils/time24";
import { Modal } from "../Modal";
import { ShopFloorIcon } from "../shop-floor/ShopFloorIcon";
import { ShopFloorDeliveryReceivingPanel } from "../shop-floor/ShopFloorDeliveryReceivingPanel";
import { TimeInput24 } from "../TimeInput24";
import { CompleteTaskModal } from "./CompleteTaskModal";
import { TaskImages } from "./TaskImages";
import { compareText, dateTimeText } from "../../i18n/intl";
import {
  TaskScheduleEditor,
  type TaskScheduleActions,
} from "./TaskScheduleEditor";
import {
  appendTaskFormValue,
  localIsoFromDateTime,
  minutesToTime,
  taskAudienceLabel,
  taskDayKey,
  taskOrderRank,
  taskStatusClass,
  taskStatusLabel,
} from "./taskUi";

function isRole(role: string): role is Role {
  return role === "OWNER" || role === "ADMIN" || role === "MANAGER" || role === "FLORIST";
}

function canManageRecurringTasks(role: string | undefined): boolean {
  return role === "OWNER" || role === "ADMIN" || role === "MANAGER";
}

const floorScheduleActions: TaskScheduleActions = {
  create: (form) => shopModeApi.floorCreateTaskSchedule(form),
  update: (id, payload) => shopModeApi.floorUpdateTaskSchedule(id, payload),
  remove: (id) => shopModeApi.floorDeleteTaskSchedule(id),
};

function compareTodayWorkItems(
  a: FloorTodayWorkItem,
  b: FloorTodayWorkItem,
  now: number,
) {
  const rank = (item: FloorTodayWorkItem) => {
    if (item.kind === "TASK") return taskOrderRank(item.task, now);
    if (item.status === "IN_PROGRESS") return 0;
    if (item.status === "SCHEDULED") {
      return new Date(item.scheduledFor).getTime() < now ? 0 : 1;
    }
    if (item.status === "COMPLETED") return 2;
    return 3;
  };
  const rankDifference = rank(a) - rank(b);
  if (rankDifference !== 0) return rankDifference;
  return (
    new Date(a.scheduledFor).getTime() - new Date(b.scheduledFor).getTime()
  );
}

interface ShopFloorTasksPanelProps {
  onWorkItemsChanged?: (items: FloorTodayWorkItem[]) => void;
}

export function ShopFloorTasksPanel({
  onWorkItemsChanged,
}: ShopFloorTasksPanelProps = {}) {
  const { t } = useT();
  const { floorActor } = useShopMode();
  const recurringAllowed = canManageRecurringTasks(floorActor?.role);
  const [view, setView] = useState<"today" | "recurring">("today");
  const [workItems, setWorkItems] = useState<FloorTodayWorkItem[]>([]);
  const [schedules, setSchedules] = useState<TaskSchedule[]>([]);
  const [scheduleAssignableUsers, setScheduleAssignableUsers] = useState<AssignableUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [recurringLoading, setRecurringLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [scheduleEditorOpen, setScheduleEditorOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<TaskSchedule | null>(null);
  const [completingTask, setCompletingTask] = useState<Task | null>(null);
  const [selectedDeliveryId, setSelectedDeliveryId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await shopModeApi.floorListTodayWorkItems();
      const now = Date.now();
      const sorted = [...list].sort((a, b) => compareTodayWorkItems(a, b, now));
      setWorkItems(sorted);
      onWorkItemsChanged?.(sorted);
    } catch (err: unknown) {
      setError(errorText(err, "tasks.couldNotLoadToday"));
    } finally {
      setLoading(false);
    }
  }, [onWorkItemsChanged]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const refreshRecurring = useCallback(async () => {
    setRecurringLoading(true);
    setError(null);
    try {
      const [recurrence, users] = await Promise.all([
        shopModeApi.floorListTaskSchedules(),
        shopModeApi.floorListTaskAssignableUsers(),
      ]);
      setSchedules(recurrence);
      setScheduleAssignableUsers(users);
    } catch (err: unknown) {
      setError(errorText(err, "tasks.couldNotLoadRecurring"));
    } finally {
      setRecurringLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view === "recurring" && recurringAllowed) void refreshRecurring();
  }, [recurringAllowed, refreshRecurring, view]);

  useEffect(() => {
    if (!recurringAllowed && view === "recurring") setView("today");
  }, [recurringAllowed, view]);

  const tasks = useMemo(
    () =>
      workItems.flatMap((item) =>
        item.kind === "TASK" ? [item.task] : [],
      ),
    [workItems],
  );

  const assignableUsers = useMemo<AssignableUser[]>(() => {
    const byId = new Map<string, AssignableUser>();
    if (floorActor && isRole(floorActor.role)) {
      byId.set(floorActor.id, {
        id: floorActor.id,
        fullName: floorActor.fullName,
        role: floorActor.role,
      });
    }
    for (const task of tasks) {
      if (task.assignee) byId.set(task.assignee.id, task.assignee);
    }
    return [...byId.values()].sort((a, b) => compareText(a.fullName, b.fullName));
  }, [floorActor, tasks]);

  if (selectedDeliveryId) {
    return (
      <ShopFloorDeliveryReceivingPanel
        deliveryId={selectedDeliveryId}
        onBack={() => setSelectedDeliveryId(null)}
        onChanged={refresh}
      />
    );
  }

  return (
    <div className="card shop-floor-tasks-card">
      <div className="day-shifts-row-head">
        <h3 style={{ margin: 0 }}>
          {view === "today" ? t("tasks.ofTheDay") : t("tasks.recurringTitle")}
        </h3>
        <div className="shop-floor-task-toolbar">
          {view === "today" && loading ? <span className="muted">{t("common.loading")}</span> : null}
          {view === "recurring" && recurringLoading ? (
            <span className="muted">{t("common.loading")}</span>
          ) : null}
          {recurringAllowed ? (
            <div className="shop-floor-task-tabs" role="tablist" aria-label={t("tasks.views")}>
              <button
                type="button"
                role="tab"
                aria-selected={view === "today"}
                className={`shop-floor-task-tab${view === "today" ? " is-active" : ""}`}
                onClick={() => setView("today")}
              >
                {t("tasks.today")}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={view === "recurring"}
                className={`shop-floor-task-tab${view === "recurring" ? " is-active" : ""}`}
                onClick={() => setView("recurring")}
              >
                {t("tasks.recurringTab")}
              </button>
            </div>
          ) : null}
          {view === "today" && !creating ? (
            <button type="button" className="button shop-floor-create-task-btn" onClick={() => setCreating(true)}>
              <ShopFloorIcon name="plus" size={20} />
              {t("tasks.create")}
            </button>
          ) : null}
          {view === "recurring" && !scheduleEditorOpen ? (
            <button
              type="button"
              className="button shop-floor-create-task-btn"
              disabled={recurringLoading}
              onClick={() => {
                setEditingSchedule(null);
                setScheduleEditorOpen(true);
              }}
            >
              <ShopFloorIcon name="plus" size={20} />
              {t("tasks.createRecurring")}
            </button>
          ) : null}
        </div>
      </div>
      {error ? <div className="alert error">{error}</div> : null}
      {view === "today" ? (
        <>
          {creating ? (
            <FloorTaskCreateForm
              assignableUsers={assignableUsers}
              onCancel={() => setCreating(false)}
              onCreated={async () => {
                setCreating(false);
                await refresh();
              }}
              onError={setError}
            />
          ) : null}
          {workItems.length === 0 && !loading ? (
            <p className="muted">{t("tasks.noneToday")}</p>
          ) : (
            workItems.map((item) =>
              item.kind === "TASK" ? (
                <FloorTaskRow
                  key={`task-${item.id}`}
                  task={item.task}
                  onComplete={() => setCompletingTask(item.task)}
                  onChanged={refresh}
                  onError={setError}
                />
              ) : (
                <FloorDeliveryTaskRow
                  key={`delivery-${item.id}`}
                  delivery={item}
                  onOpen={() => setSelectedDeliveryId(item.id)}
                />
              ),
            )
          )}
        </>
      ) : (
        <FloorRecurringTaskList
          schedules={schedules}
          loading={recurringLoading}
          onEdit={(schedule) => {
            setEditingSchedule(schedule);
            setScheduleEditorOpen(true);
          }}
        />
      )}

      {completingTask ? (
        <CompleteTaskModal
          task={completingTask}
          mode="floor"
          onClose={() => setCompletingTask(null)}
          onCompleted={refresh}
        />
      ) : null}

      {scheduleEditorOpen && recurringAllowed ? (
        <Modal
          title={editingSchedule ? t("tasks.editRecurring") : t("tasks.createRecurring")}
          onClose={() => {
            setScheduleEditorOpen(false);
            setEditingSchedule(null);
          }}
        >
          <TaskScheduleEditor
            schedule={editingSchedule ?? undefined}
            assignableUsers={scheduleAssignableUsers}
            actions={floorScheduleActions}
            onCancel={() => {
              setScheduleEditorOpen(false);
              setEditingSchedule(null);
            }}
            onError={setError}
            onSaved={async () => {
              await refreshRecurring();
              await refresh();
              setScheduleEditorOpen(false);
              setEditingSchedule(null);
            }}
            onDeleted={async () => {
              await refreshRecurring();
              await refresh();
              setScheduleEditorOpen(false);
              setEditingSchedule(null);
            }}
          />
        </Modal>
      ) : null}
    </div>
  );
}

function FloorDeliveryTaskRow({
  delivery,
  onOpen,
}: {
  delivery: FloorDeliveryTodayWorkItem;
  onOpen: () => void;
}) {
  const { t } = useT();
  const overdue =
    delivery.status === "SCHEDULED" &&
    new Date(delivery.scheduledFor).getTime() < Date.now();
  // A RAW PRISMA ENUM WAS ON SCREEN HERE. `IN_PROGRESS` was lower-cased and
  // had its underscore swapped for a space, which reads well enough in
  // English and cannot work at all in Russian. Same finding as S3's receiving
  // header, and the ratchet missed it again for the same reason: no quoted
  // string, no space. The words are S3's, reused rather than re-keyed.
  const statusLabel = overdue
    ? t("tasks.overdue")
    : delivery.status === "COMPLETED"
      ? t("receiving.deliveryCompleted")
      : delivery.status === "CANCELED"
        ? t("receiving.deliveryCanceled")
        : delivery.status === "IN_PROGRESS"
          ? t("receiving.deliveryInProgress")
          : t("receiving.deliveryScheduled");
  const statusClass = overdue
    ? "badge danger"
    : delivery.status === "COMPLETED"
      ? "badge success"
      : delivery.status === "IN_PROGRESS"
        ? "badge primary"
        : delivery.status === "CANCELED"
          ? "badge danger"
          : "badge muted";
  const active =
    delivery.status === "SCHEDULED" || delivery.status === "IN_PROGRESS";
  const stateClass =
    delivery.status === "CANCELED"
      ? "is-cancelled"
      : `is-${delivery.status.toLowerCase()}`;

  return (
    <div className={`day-shifts-row shop-floor-task-card shop-floor-delivery-task-card ${stateClass}`}>
      <div className="shop-floor-task-card__header">
        <strong>{delivery.name}</strong>
        <span className={statusClass}>{statusLabel}</span>
      </div>
      <div className="shop-floor-task-card__meta-row">
        <ShopFloorIcon name="delivery" size={21} />
        <span>{delivery.supplier.name}</span>
      </div>
      <div className="shop-floor-task-card__divider" />
      <div className="shop-floor-task-card__detail-row">
        <ShopFloorIcon name="calendar" size={21} />
        <span>{dateTimeText(new Date(delivery.scheduledFor))}</span>
      </div>
      <div className="shop-floor-task-card__detail-row">
        <ShopFloorIcon name="stock" size={21} />
        <span>
          {t("receiving.linesConfirmed", {
            done: delivery.confirmedLineCount,
            total: delivery.totalLineCount,
          })}
        </span>
      </div>
      {active ? (
        <div className="button-row">
          <button
            type="button"
            className="button shop-floor-task-complete-btn"
            onClick={onOpen}
          >
            <ShopFloorIcon name="delivery" size={20} />
            {delivery.status === "IN_PROGRESS"
              ? t("tasks.continueReceiving")
              : t("tasks.receiveStock")}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function FloorRecurringTaskList({
  schedules,
  loading,
  onEdit,
}: {
  schedules: TaskSchedule[];
  loading: boolean;
  onEdit: (schedule: TaskSchedule) => void;
}) {
  const { t, language } = useT();
  if (!loading && schedules.length === 0) {
    return <p className="muted">{t("tasks.noRecurring")}</p>;
  }

  return (
    <>
      {schedules.map((schedule) => (
        <div key={schedule.id} className="day-shifts-row shop-floor-task-card shop-floor-schedule-card">
          <div className="shop-floor-task-card__header">
            <strong>{schedule.name}</strong>
            <span className={schedule.isActive ? "badge success" : "badge muted"}>
              {schedule.isActive ? "active" : "paused"}
            </span>
          </div>
          <div className="shop-floor-task-card__meta-row">
            <ShopFloorIcon name="store" size={21} />
            <span>{schedule.assignee?.fullName ?? t("taskUi.wholeShop")}</span>
          </div>
          <div className="shop-floor-task-card__divider" />
          <div className="shop-floor-task-card__detail-row">
            <ShopFloorIcon name="calendar" size={21} />
            <span>
              {t("tasks.ruleAtTime", {
                rule: recurringRuleLabel(t, intlLocale(language), schedule),
                time: minutesToTime(schedule.timeOfDayMinutes),
              })}
            </span>
          </div>
          {schedule.description ? (
            <div className="shop-floor-task-card__detail-row">
              <ShopFloorIcon name="document" size={21} />
              <span>{schedule.description}</span>
            </div>
          ) : null}
          <div className="button-row">
            <button type="button" className="button plain" onClick={() => onEdit(schedule)}>
              {t("tasks.edit")}
            </button>
          </div>
        </div>
      ))}
    </>
  );
}

/**
 * G-85 S4 — "Every Mon, Wed", in the kiosk's language.
 *
 * THE WEEKDAY NAMES COME FROM `Intl`, NOT THE DICTIONARY. Seven names across
 * five languages is thirty-five keys for something every platform already
 * knows, and a hand-typed list is a list someone eventually gets wrong. The
 * ratchet never flagged the old `["Sun", "Mon", …]` because single words in an
 * array carry no spaces — a reminder that the count is a floor, not a ceiling.
 *
 * 7 January 2024 was a Sunday, so `7 + day` walks Sunday..Saturday in the
 * order the API sends `weekdays`. Formatted in UTC so a tablet an hour either
 * side of the line cannot shift a name by a day.
 *
 * The plural forms matter here: Russian needs "Каждые 2 дня" and "Каждые 5
 * дней", which no suffix rule on an English word can produce.
 */
function recurringRuleLabel(
  t: TranslateFn,
  locale: string,
  schedule: TaskSchedule,
): string {
  if (schedule.recurrenceKind === "EVERY_N_DAYS") {
    const days = schedule.intervalDays ?? 1;
    return days === 1 ? t("tasks.everyDay") : t("tasks.everyNDays", { count: days });
  }

  const weekday = new Intl.DateTimeFormat(locale, {
    weekday: "short",
    timeZone: "UTC",
  });
  const days = schedule.weekdays
    .map((day) => weekday.format(new Date(Date.UTC(2024, 0, 7 + day))))
    .filter(Boolean)
    .join(", ");
  const interval = schedule.intervalWeeks ?? 1;
  return interval === 1
    ? t("tasks.everyWeekdays", { days })
    : t("tasks.everyNWeeksOn", { count: interval, days });
}

function FloorTaskRow({
  task,
  onComplete,
  onChanged,
  onError,
}: {
  task: Task;
  onComplete: () => void;
  onChanged: () => Promise<void>;
  onError: (message: string | null) => void;
}) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const pending = task.status === "PENDING";
  const statusLabel = taskStatusLabel(task);
  const statusClass = statusLabel === "overdue" ? "badge danger" : taskStatusClass(task.status);

  const cancel = async () => {
    const reason = prompt(translate("tasks.cancelPrompt", { name: task.name }));
    if (reason === null) return;
    setBusy(true);
    onError(null);
    try {
      await shopModeApi.floorCancelTask(task.id, { reason: reason.trim() || undefined });
      await onChanged();
    } catch (err: unknown) {
      onError(errorText(err, "tasks.couldNotCancel"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`day-shifts-row shop-floor-task-card is-${task.status.toLowerCase()}`}>
      <div className="shop-floor-task-card__header">
        <strong>{task.name}</strong>
        <span className={statusClass}>{statusLabel}</span>
      </div>
      <div className="shop-floor-task-card__meta-row">
        <ShopFloorIcon name="store" size={21} />
        <span>{taskAudienceLabel(task)}</span>
      </div>
      <div className="shop-floor-task-card__divider" />
      {task.description ? (
        <div className="shop-floor-task-card__detail-row">
          <ShopFloorIcon name="document" size={21} />
          <span>{task.description}</span>
        </div>
      ) : null}
      {task.notes ? (
        <div className="shop-floor-task-card__detail-row">
          <ShopFloorIcon name="notes" size={21} />
          <span>{task.notes}</span>
        </div>
      ) : null}
      <TaskImages task={task} mode="floor" />
      {task.requireCompletionPhoto ? (
        <span className="badge primary">{t("tasks.photoRequired")}</span>
      ) : null}
      {pending ? (
        <div className="button-row">
          <button
            type="button"
            className="button shop-floor-task-cancel-btn"
            onClick={() => void cancel()}
            disabled={busy}
          >
            <ShopFloorIcon name="close" size={19} />
            {busy ? t("tasks.cancelling") : t("common.cancel")}
          </button>
          <button
            type="button"
            className="button shop-floor-task-complete-btn"
            onClick={onComplete}
            disabled={busy}
          >
            <ShopFloorIcon name="check" size={20} />
            {t("tasks.complete")}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function FloorTaskCreateForm({
  assignableUsers,
  onCancel,
  onCreated,
  onError,
}: {
  assignableUsers: AssignableUser[];
  onCancel: () => void;
  onCreated: () => Promise<void>;
  onError: (message: string | null) => void;
}) {
  const { t } = useT();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [time, setTime] = useState("09:00");
  const [assigneeUserId, setAssigneeUserId] = useState("");
  const [requireCompletionPhoto, setRequireCompletionPhoto] = useState(false);
  const [referenceImage, setReferenceImage] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    onError(null);
    try {
      const normalizedTime = parseFlexibleTime24(time);
      if (!name.trim()) {
        onError(t("tasks.nameRequired"));
        return;
      }
      if (!normalizedTime) {
        onError(t("tasks.timeInvalid"));
        return;
      }
      const form = new FormData();
      form.append("name", name.trim());
      form.append("scheduledFor", localIsoFromDateTime(taskDayKey(new Date()), normalizedTime));
      form.append("requireCompletionPhoto", String(requireCompletionPhoto));
      appendTaskFormValue(form, "description", description);
      appendTaskFormValue(form, "notes", notes);
      if (assigneeUserId) form.append("assigneeUserId", assigneeUserId);
      if (referenceImage) form.append("image", referenceImage);
      await shopModeApi.floorCreateTask(form);
      await onCreated();
    } catch (err: unknown) {
      onError(errorText(err, "tasks.couldNotCreate"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="day-shifts-row" onSubmit={(event) => void submit(event)}>
      <strong>{t("tasks.create")}</strong>
      <div className="modal-form">
        <div className="field full">
          <label htmlFor="floor-task-name">{t("tasks.name")}</label>
          <input
            id="floor-task-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
            required
            maxLength={200}
          />
        </div>
        <div className="field">
          <label>{t("tasks.time")}</label>
          <TimeInput24 value={time} onChange={setTime} disabled={busy} />
        </div>
        <div className="field">
          <label htmlFor="floor-task-assignee">{t("tasks.assignee")}</label>
          <select
            id="floor-task-assignee"
            value={assigneeUserId}
            onChange={(e) => setAssigneeUserId(e.target.value)}
            disabled={busy}
          >
            <option value="">{t("taskUi.wholeShop")}</option>
            {assignableUsers.map((user) => (
              <option key={user.id} value={user.id}>
                {user.fullName}
              </option>
            ))}
          </select>
        </div>
        <div className="field full">
          <label htmlFor="floor-task-description">{t("tasks.description")}</label>
          <textarea
            id="floor-task-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={busy}
            rows={2}
          />
        </div>
        <div className="field full">
          <label htmlFor="floor-task-notes">{t("tasks.notes")}</label>
          <textarea
            id="floor-task-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={busy}
            rows={2}
          />
        </div>
        <div className="field full">
          <label htmlFor="floor-task-reference-image">{t("tasks.referencePhoto")}</label>
          <input
            id="floor-task-reference-image"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            onChange={(e) => setReferenceImage(e.target.files?.[0] ?? null)}
            disabled={busy}
          />
        </div>
        <label
          className="field full"
          style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8 }}
        >
          <input
            type="checkbox"
            checked={requireCompletionPhoto}
            onChange={(e) => setRequireCompletionPhoto(e.target.checked)}
            disabled={busy}
          />
          {t("tasks.requireCompletionPhoto")}
        </label>
      </div>
      <div className="button-row">
        <button type="button" className="button plain" onClick={onCancel} disabled={busy}>
          {t("common.cancel")}
        </button>
        <button type="submit" className="button" disabled={busy}>
          {busy ? t("box.creating") : t("tasks.create")}
        </button>
      </div>
    </form>
  );
}
